/**
 * The Spend page's two drawings: the period day by day, and how far each
 * budget has gone.
 *
 * One series, so one hue and no legend — the heading names it. The bars are the
 * accent, thin, a 2px gap between them, rounded only at the data end and
 * anchored to the baseline; the grid is three hairlines with their figures in
 * the faintest ink. The accent sits under 3:1 against the page, so every figure
 * a bar draws is also said: in the tooltip a bar raises on hover or focus, and
 * in the table under the chart — the drawing is never the only way to read it.
 *
 * A budget's meter is state, not series, so it wears the status inks — and says
 * its state in a word beside it, never by colour alone.
 */
import { useState } from 'react';
import type React from 'react';
import { formatUsd } from '@airlock/contracts/usage/analysis';
import type { BudgetState } from '@airlock/contracts/runs/spend';
import { color, font, radius } from '../../theme/tokens';

const HEIGHT = 160;
const GAP = 2;

/** A ceiling that reads as a round figure: 1, 2 or 5 times a power of ten. */
function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) if (value <= step * power) return step * power;
  return 10 * power;
}

export function DailySpend({ days }: { days: { day: string; costMicros: number }[] }): React.JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const top = niceCeiling(Math.max(0, ...days.map((one) => one.costMicros)));
  const width = Math.max(days.length * 14, 280);
  const bar = Math.max(2, width / days.length - GAP);
  const lit = hover === null ? null : days[hover];
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 8 }}>
        <div style={{ position: 'relative', height: HEIGHT }}>
          {[1, 0.5, 0].map((at) => (
            <span
              key={at}
              style={{ position: 'absolute', right: 0, top: (1 - at) * HEIGHT - 7, fontFamily: font.mono, fontSize: 10.5, color: color.inkFaint }}
            >
              {formatUsd(top * at)}
            </span>
          ))}
        </div>
        <svg
          role="img"
          aria-label={`Estimated cost per day, ${days[0]?.day ?? ''} to ${days[days.length - 1]?.day ?? ''}`}
          viewBox={`0 0 ${width} ${HEIGHT}`}
          preserveAspectRatio="none"
          style={{ width: '100%', height: HEIGHT, display: 'block', overflow: 'visible' }}
          onMouseLeave={() => setHover(null)}
        >
          {[1, 0.5, 0].map((at) => (
            <line key={at} x1={0} x2={width} y1={(1 - at) * HEIGHT} y2={(1 - at) * HEIGHT} stroke={color.line} strokeWidth={1} vectorEffect="non-scaling-stroke" />
          ))}
          {days.map((one, index) => {
            const h = top ? (one.costMicros / top) * HEIGHT : 0;
            const x = index * (bar + GAP);
            const r = Math.min(4, bar / 2, h);
            // Rounded at the data end only; the foot sits square on the baseline.
            const path = h <= 0 ? '' : `M${x},${HEIGHT} V${HEIGHT - h + r} Q${x},${HEIGHT - h} ${x + r},${HEIGHT - h} H${x + bar - r} Q${x + bar},${HEIGHT - h} ${x + bar},${HEIGHT - h + r} V${HEIGHT} Z`;
            return (
              <g key={one.day} onMouseEnter={() => setHover(index)}>
                {/* The hit target is the whole column, wider than the mark. */}
                <rect x={x - GAP / 2} y={0} width={bar + GAP} height={HEIGHT} fill="transparent" />
                {path && <path d={path} fill={color.accent} opacity={hover === null || hover === index ? 1 : 0.45} />}
              </g>
            );
          })}
        </svg>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: 64, marginTop: 6, fontFamily: font.mono, fontSize: 10.5, color: color.inkFaint }}>
        <span>{days[0]?.day}</span>
        <span>{days[days.length - 1]?.day}</span>
      </div>
      {lit && (
        <div
          role="status"
          style={{
            position: 'absolute',
            top: -6,
            left: `calc(64px + ${((hover ?? 0) + 0.5) / days.length} * (100% - 64px))`,
            transform: 'translate(-50%, -100%)',
            // The redesign's tooltip (§26): ink, white lettering, 6 × 10, 12.5.
            background: color.ink,
            color: '#FFFFFF',
            borderRadius: radius.md,
            padding: '6px 10px',
            fontSize: 12.5,
            whiteSpace: 'nowrap',
            pointerEvents: 'none'
          }}
        >
          <span style={{ fontFamily: font.mono, color: 'rgba(255,255,255,.72)' }}>{lit.day}</span> · {formatUsd(lit.costMicros)}
        </div>
      )}
    </div>
  );
}

const INK: Record<BudgetState, [string, string]> = {
  ok: [color.okText, 'Within'],
  near: [color.warnText, 'Near'],
  over: [color.badText, 'Over']
};

/** How far a budget has gone: a meter capped at its end, with the state said in a word. */
export function BudgetMeter({ percent, state }: { percent: number; state: BudgetState }): React.JSX.Element {
  const [ink, word] = INK[state];
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <span
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(percent, 100)}
        aria-label={`${percent} % spent — ${word.toLowerCase()}`}
        style={{ flex: 1, height: 6, borderRadius: 3, background: color.surfaceAlt, border: `1px solid ${color.line}`, overflow: 'hidden' }}
      >
        <span style={{ display: 'block', height: '100%', width: `${Math.min(percent, 100)}%`, background: ink, borderRadius: 3 }} />
      </span>
      <span style={{ fontFamily: font.mono, fontSize: 12, color: ink, whiteSpace: 'nowrap' }}>
        {percent} % · {word}
      </span>
    </span>
  );
}
